import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SuperAdminNav from '../components/SuperAdminNav';

function formatCurrency(amount) {
  return '$' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusBadge({ status, comped, manuallySuspended, trialEnd }) {
  let label = status;
  let cls = 'status-badge';
  if (comped) { label = 'comped'; cls += ' status-comped'; }
  else if (manuallySuspended) { label = 'suspended (manual)'; cls += ' status-suspended'; }
  else if (status === 'active') cls += ' status-active';
  else if (status === 'trialing') { cls += ' status-trialing'; label = trialEnd ? `Free Trial - Ends ${new Date(trialEnd).toLocaleDateString()}` : 'Free Trial'; }
  else if (status === 'past_due') cls += ' status-pastdue';
  else if (status === 'suspended') cls += ' status-suspended';
  else if (status === 'canceled') cls += ' status-canceled';
  return <span className={cls}>{label}</span>;
}

// ============================================================
// ACCOUNTS TABLE (shared between Clubs and Teams tabs)
// ============================================================
function AccountsTable({ accounts, onSelect, showInactive, setShowInactive, loading, label }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = accounts.filter(a => {
    if (search && !a.account_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all') {
      if (statusFilter === 'comped') { if (!a.comped) return false; }
      else if (a.status !== statusFilter) return false;
    }
    if (!showInactive && a.deactivated) return false;
    return true;
  });

  return (
    <div>
      <div className="super-filters">
        <input
          className="form-input"
          placeholder="Search by name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        <select
          className="form-input"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ maxWidth: 180 }}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="past_due">Past due</option>
          <option value="suspended">Suspended</option>
          <option value="canceled">Canceled</option>
          <option value="comped">Comped</option>
        </select>
        <label className="super-dormant-toggle">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show Inactive {label}
        </label>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>No {label.toLowerCase()} match your filters.</div>
      ) : (
        <div className="super-table-wrap">
          <table className="super-table">
            <thead>
              <tr>
                <th>{label === 'Clubs' ? 'Club' : 'Team'}</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Mo. Equiv.</th>
                <th>Status</th>
                <th>Players</th>
                <th>Active %</th>
                <th>Last Activity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} onClick={() => onSelect(a)} className="super-table-row">
                  <td>
                    <div className="super-account-name">{a.account_name}</div>
                  </td>
                  <td>
                    <div>{a.plan_name}</div>
                    <div className="super-account-type">{a.billing_interval}</div>
                  </td>
                  <td>{a.comped ? 'Comped' : `${formatCurrency(a.amount)}/${a.billing_interval === 'annual' ? 'yr' : 'mo'}`}</td>
                  <td>{formatCurrency(a.monthly_equivalent)}</td>
                  <td><StatusBadge status={a.status} comped={a.comped} manuallySuspended={a.manually_suspended} trialEnd={a.trial_end} /></td>
                  <td>{a.no_subscription ? a.player_count : `${a.player_count} of ${a.player_cap}`}</td>
                  <td>{a.active_percent}%</td>
                  <td>
                    <div>{formatDate(a.last_activity)}</div>
                    {a.deactivated && <span className="dormant-flag" style={{ background: '#b91c1c' }}>DEACTIVATED</span>}
                    {!a.deactivated && a.dormant && <span className="dormant-flag">DORMANT</span>}
                    {a.is_free && <span className="dormant-flag" style={{ background: '#16a34a' }}>FREE</span>}
                  </td>
                  <td><span className="super-arrow">&rsaquo;</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// CLUB/TEAM PLAYERS TABLE (search-first)
// ============================================================
function ClubTeamPlayersTable({ apiFetch, startImpersonation, navigate }) {
  const [search, setSearch] = useState('');
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiFetch('/api/super/club-team-players/stats')
      .then(data => setStats(data))
      .catch(err => console.error('Stats load error:', err));
  }, [apiFetch]);

  const doSearch = useCallback(async (query, inactive) => {
    if (!query || query.trim().length < 2) { setPlayers([]); setSearched(false); return; }
    setLoading(true);
    try {
      const data = await apiFetch(`/api/super/club-team-players?q=${encodeURIComponent(query.trim())}&showInactive=${inactive}`);
      setPlayers(data.players);
      setSearched(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    const timeout = setTimeout(() => doSearch(search, showInactive), 350);
    return () => clearTimeout(timeout);
  }, [search, showInactive, doSearch]);

  const handleLoginAs = async (playerId) => {
    try {
      const data = await apiFetch('/api/super/impersonate', {
        method: 'POST',
        body: JSON.stringify({ user_id: playerId, user_type: 'player' }),
      });
      startImpersonation(data.token, data.user, data.teams);
      navigate('/today');
    } catch (err) {
      console.error('Login-as error:', err);
    }
  };

  return (
    <div>
      {stats && (
        <>
          <div className="super-totals">
            <div className="super-total-card">
              <div className="super-total-value">{stats.total.toLocaleString()}</div>
              <div className="super-total-label">Total Players</div>
            </div>
            <div className="super-total-card">
              <div className="super-total-value">{stats.today}</div>
              <div className="super-total-label">Added Today</div>
            </div>
            <div className="super-total-card">
              <div className="super-total-value">{stats.last_7_days}</div>
              <div className="super-total-label">Last 7 Days</div>
            </div>
          </div>

          {!searched && stats.recent.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem', color: 'var(--text-muted)' }}>Recent Signups</h4>
              <div className="super-table-wrap">
                <table className="super-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Team</th>
                      <th>Club</th>
                      <th>Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent.map((r, i) => (
                      <tr key={i} className="super-table-row">
                        <td>{r.name}</td>
                        <td style={{ fontSize: '0.85rem' }}>{r.email || '—'}</td>
                        <td>{r.team_name}</td>
                        <td>{r.club_name}</td>
                        <td>{formatDate(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <div className="super-filters">
        <input
          className="form-input"
          placeholder="Search by name, email, team, or club..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
          autoFocus
        />
        <label className="super-dormant-toggle">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Include Inactive
        </label>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : !searched ? (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>
          Type at least 2 characters to search club/team players.
        </div>
      ) : players.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>No players found.</div>
      ) : (
        <div className="super-table-wrap">
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 8 }}>
            {players.length}{players.length === 100 ? '+' : ''} result{players.length !== 1 ? 's' : ''}
          </div>
          <table className="super-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Team</th>
                <th>Club</th>
                <th>Age Group</th>
                <th>Added</th>
                {showInactive && <th>Status</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {players.map(p => (
                <tr key={p.id} className="super-table-row">
                  <td>{p.name}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{p.username || '—'}</td>
                  <td style={{ fontSize: '0.85rem' }}>{p.email || '—'}</td>
                  <td>{p.team_name}</td>
                  <td>{p.club_name}</td>
                  <td>{p.age_group}</td>
                  <td>{formatDate(p.created_at)}</td>
                  {showInactive && <td><span className={`status-badge ${p.status === 'active' ? 'status-active' : 'status-canceled'}`}>{p.status}</span></td>}
                  <td>
                    {p.status === 'active' && (
                      <button className="btn btn-sm btn-outline" onClick={() => handleLoginAs(p.id)} style={{ fontSize: '0.75rem', padding: '2px 8px', whiteSpace: 'nowrap' }}>
                        Login as
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// INDIVIDUALS / FAMILIES TABLE
// ============================================================
function IndividualsTable({ accounts, loading, onSelect }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = accounts.filter(a => {
    if (search) {
      const q = search.toLowerCase();
      if (!a.name.toLowerCase().includes(q) && !a.email.toLowerCase().includes(q)) return false;
    }
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    return true;
  });

  return (
    <div>
      <div className="super-filters">
        <input
          className="form-input"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        <select
          className="form-input"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ maxWidth: 180 }}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="past_due">Past due</option>
          <option value="canceled">Canceled</option>
          <option value="pending">Pending</option>
          <option value="none">No subscription</option>
        </select>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>No individual/family accounts match your filters.</div>
      ) : (
        <div className="super-table-wrap">
          <table className="super-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Interval</th>
                <th>Status</th>
                <th>Kids</th>
                <th>Payment</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className="super-table-row" onClick={() => onSelect(a)}>
                  <td><div className="super-account-name">{a.name}</div></td>
                  <td style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{a.email}</td>
                  <td>{a.plan_name}</td>
                  <td>{a.billing_interval || '—'}</td>
                  <td>
                    <span className={`status-badge ${
                      a.status === 'active' ? 'status-active' :
                      a.status === 'trialing' ? 'status-trialing' :
                      a.status === 'past_due' ? 'status-pastdue' :
                      a.status === 'canceled' ? 'status-canceled' :
                      a.status === 'pending' ? 'status-trialing' :
                      'status-canceled'
                    }`}>
                      {a.status}
                    </span>
                  </td>
                  <td>{a.kid_count}</td>
                  <td>{a.has_payment ? <span style={{ color: 'var(--success)' }}>Yes</span> : <span style={{ color: 'var(--text-muted)' }}>No</span>}</td>
                  <td>{formatDate(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// INDIVIDUAL ACCOUNT DETAIL VIEW
// ============================================================
function IndividualDetail({ parentId, onBack, apiFetch }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [actionError, setActionError] = useState('');

  const loadDetail = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/super/individual-accounts/${parentId}`);
      setDetail(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [parentId, apiFetch]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const handleDelete = async () => {
    if (!window.confirm('PERMANENTLY DELETE this parent account and ALL kid data (players, completions, badges)? This cannot be undone.')) return;
    if (!window.confirm('Are you absolutely sure? This is irreversible.')) return;
    setActionLoading('delete');
    setActionError('');
    try {
      const data = await apiFetch(`/api/super/accounts/parent_${parentId}`, { method: 'DELETE' });
      setActionMsg(data.message || 'Account deleted.');
      onBack();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setActionLoading('');
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!detail) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Failed to load account.</div>;

  return (
    <div>
      <button className="btn btn-outline btn-sm" onClick={onBack} style={{ marginBottom: 16 }}>&larr; Back</button>
      <h2 className="page-title" style={{ marginBottom: 4 }}>{detail.name}</h2>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: 16 }}>{detail.email}</div>

      {actionMsg && <div style={{ color: 'var(--success)', marginBottom: 16, padding: '12px 16px', background: 'rgba(46,204,113,0.1)', borderRadius: 8 }}>{actionMsg}</div>}
      {actionError && <div style={{ color: 'var(--danger)', marginBottom: 16, padding: '12px 16px', background: 'rgba(239,68,68,0.1)', borderRadius: 8 }}>{actionError}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Subscription</h3>
        <div className="super-detail-grid">
          <div><span className="detail-label">Plan:</span> {detail.plan_name}</div>
          <div><span className="detail-label">Interval:</span> {detail.billing_interval || '—'}</div>
          <div><span className="detail-label">Status:</span> <span className={`status-badge ${
            detail.status === 'active' ? 'status-active' :
            detail.status === 'trialing' ? 'status-trialing' :
            detail.status === 'past_due' ? 'status-pastdue' :
            detail.status === 'canceled' ? 'status-canceled' :
            'status-canceled'
          }`}>{detail.status}</span></div>
          <div><span className="detail-label">Payment on file:</span> {detail.has_payment ? 'Yes' : 'No'}</div>
          <div><span className="detail-label">Created:</span> {formatDate(detail.created_at)}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Kids ({detail.kids.length})</h3>
        {detail.kids.length === 0 ? (
          <div style={{ color: 'var(--text-muted)' }}>No kids.</div>
        ) : (
          <div className="super-table-wrap">
            <table className="super-table">
              <thead><tr><th>Name</th><th>Username</th><th>Created</th></tr></thead>
              <tbody>
                {detail.kids.map(k => (
                  <tr key={k.id}>
                    <td>{k.first_name} {k.last_name}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{k.generated_username}</td>
                    <td>{formatDate(k.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="super-action-row">
        <button
          className="btn btn-sm btn-danger"
          onClick={handleDelete}
          disabled={!!actionLoading}
          style={{ opacity: 0.7 }}
        >
          {actionLoading === 'delete' ? 'Deleting...' : 'Delete Forever'}
        </button>
        <span style={{ color: '#b91c1c', fontSize: '0.8rem' }}>Permanently removes parent account and all kid data.</span>
      </div>
    </div>
  );
}

// ============================================================
// ACCOUNT DETAIL VIEW
// ============================================================
function AccountDetail({ accountId, onBack, apiFetch }) {
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [actionError, setActionError] = useState('');
  const [trialDays, setTrialDays] = useState(14);
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountForm, setDiscountForm] = useState({ type: 'percent_off', value: '', duration: 'once', duration_in_months: 3 });
  const [promoType, setPromoType] = useState('scholarship');
  const [promoDiscount, setPromoDiscount] = useState(50);
  const [promoTeamId, setPromoTeamId] = useState('');
  const [promoCount, setPromoCount] = useState(5);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoLinks, setPromoLinks] = useState([]);
  const [promoError, setPromoError] = useState('');
  const [promoCopied, setPromoCopied] = useState(false);

  const loadDetail = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/super/accounts/${accountId}`);
      setDetail(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [accountId, apiFetch]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const doAction = async (path, body, msg) => {
    setActionLoading(path);
    setActionMsg('');
    setActionError('');
    try {
      if (path === 'delete') {
        const data = await apiFetch(`/api/super/accounts/${accountId}`, {
          method: 'DELETE',
        });
        setActionMsg(data.message || msg || 'Deleted.');
        onBack();
        return;
      }
      const data = await apiFetch(`/api/super/accounts/${accountId}/${path}`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setActionMsg(data.message || msg || 'Done.');
      loadDetail();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setActionLoading('');
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const data = await apiFetch('/api/super/impersonate', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      });
      startImpersonation(data.token, data.user, data.teams);
      if (data.user.role === 'club_admin') {
        navigate('/club');
      } else {
        navigate('/admin');
      }
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleDiscount = async (e) => {
    e.preventDefault();
    await doAction('discount', discountForm, 'Discount applied.');
    setShowDiscount(false);
  };

  const handleGeneratePromos = async (e) => {
    e.preventDefault();
    setPromoLoading(true);
    setPromoError('');
    setPromoLinks([]);
    setPromoCopied(false);
    try {
      const body = { team_id: promoTeamId, type: promoType, count: parseInt(promoCount) };
      if (promoType === 'discount') body.discount_percent = parseInt(promoDiscount);
      const data = await apiFetch('/api/super/promo-codes/generate', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setPromoLinks(data.codes);
    } catch (err) {
      setPromoError(err.message);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCopyAllLinks = () => {
    const text = promoLinks.map(l => l.url).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setPromoCopied(true);
      setTimeout(() => setPromoCopied(false), 2000);
    });
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!detail) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Failed to load account.</div>;

  const { billing, teams, engagement, impersonatable_users } = detail;

  return (
    <div>
      <button className="btn btn-outline btn-sm" onClick={onBack} style={{ marginBottom: 16 }}>&larr; Back</button>
      <h2 className="page-title" style={{ marginBottom: 4 }}>{detail.account_name}</h2>
      {impersonatable_users.length > 0 && (
        <div style={{ color: 'var(--text-mid, #52627a)', fontSize: '0.95rem', marginBottom: 16 }}>
          {impersonatable_users[0].email}
        </div>
      )}

      {actionMsg && <div className="super-action-msg success">{actionMsg}</div>}
      {actionError && <div className="super-action-msg error">{actionError}</div>}

      {/* Billing Summary */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Billing</h3>
        <div className="super-detail-grid">
          <div><span className="detail-label">Plan:</span> {billing.plan_name} ({billing.billing_interval})</div>
          <div><span className="detail-label">Amount:</span> {billing.is_free ? 'Free' : billing.comped ? 'Comped' : formatCurrency(billing.amount)}{!billing.comped && !billing.is_free && `/${billing.billing_interval === 'annual' ? 'yr' : 'mo'}`}</div>
          <div><span className="detail-label">Status:</span> <StatusBadge status={billing.status} comped={billing.comped} manuallySuspended={billing.manually_suspended} trialEnd={billing.trial_end} />{billing.deactivated && <span className="dormant-flag" style={{ background: '#b91c1c', marginLeft: 8 }}>DEACTIVATED</span>}</div>
          {billing.status !== 'trialing' && <div><span className="detail-label">Next billing:</span> {formatDate(billing.current_period_end)}</div>}
          <div><span className="detail-label">Card:</span> {billing.card_brand ? `${billing.card_brand} ****${billing.card_last4}` : '—'}</div>
          <div><span className="detail-label">Player cap:</span> {billing.player_cap}{billing.addon_quantity > 0 && ` (${billing.addon_quantity} add-on)`}</div>
          {billing.comped && !billing.is_free && <div><span className="detail-label">Comped since:</span> {formatDate(billing.comped_at)}</div>}
        </div>
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!billing.is_free} onChange={() => {
              if (billing.is_free) {
                if (window.confirm('Remove free status? A 30-day trial will begin.')) {
                  doAction('toggle-free', { is_free: false });
                }
              } else {
                if (window.confirm('Make this a free account? It will operate as club-pays with no billing.')) {
                  const expiresInput = window.prompt('Optional: Enter expiration date (YYYY-MM-DD) or leave blank for no expiration:');
                  const expires_at = expiresInput && expiresInput.trim() ? expiresInput.trim() : null;
                  doAction('toggle-free', { is_free: true, expires_at });
                }
              }
            }} disabled={!!actionLoading} />
            <span className="detail-label" style={{ margin: 0 }}>Free Account</span>
          </label>
          {billing.is_free && (
            <div style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              {billing.free_expires_at ? `Expires ${formatDate(billing.free_expires_at)}` : 'No expiration'}
              {billing.free_granted_at && ` — granted ${formatDate(billing.free_granted_at)}`}
              <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: '0.75rem' }}>(250/team, 500/club)</span>
            </div>
          )}
        </div>
      </div>

      {/* Engagement Summary */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Engagement</h3>
        <div className="super-detail-grid">
          <div><span className="detail-label">Total players:</span> {engagement.total_players}</div>
          <div><span className="detail-label">Active this week:</span> {engagement.active_this_week} ({engagement.active_percent}%)</div>
          <div><span className="detail-label">Completion rate:</span> {engagement.completion_rate}%</div>
          <div><span className="detail-label">Dormant teams:</span> {engagement.dormant_teams}</div>
        </div>
      </div>

      {/* Teams */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Teams ({teams.length})</h3>
        {teams.length === 0 ? (
          <div style={{ color: 'var(--text-muted)' }}>No teams.</div>
        ) : (
          <table className="super-table super-table-compact">
            <thead>
              <tr>
                <th>Team</th>
                <th>Players</th>
                <th>Active</th>
                <th>Completion</th>
                <th>Last Activity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {teams.map(t => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td>{t.player_count}</td>
                  <td>{t.active_this_week}</td>
                  <td>{t.completion_rate}%</td>
                  <td>
                    {formatDate(t.last_activity)}
                    {t.dormant && <span className="dormant-flag" style={{ marginLeft: 8 }}>DORMANT</span>}
                  </td>
                  <td><span className={`team-status-dot ${t.status}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Impersonation */}
      {impersonatable_users.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12 }}>View As (Read-Only)</h3>
          <div className="super-impersonate-list">
            {impersonatable_users.map(u => (
              <div key={u.id} className="super-impersonate-item">
                <div>
                  <strong>{u.first_name} {u.last_name}</strong>
                  <span className="super-account-type" style={{ marginLeft: 8 }}>{u.role === 'club_admin' ? 'Club Admin' : 'Coach'}</span>
                </div>
                <button className="btn btn-sm btn-outline" onClick={() => handleImpersonate(u.id)}>View as</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Management Actions */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Actions</h3>
        {billing.no_subscription && (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 12 }}>
            This account has no billing subscription. Billing actions (trial, discount) are not available until a plan is selected.
          </div>
        )}
        <div className="super-actions">
          {/* Extend Trial */}
          {!billing.no_subscription && (billing.status === 'trialing' || billing.trial_end) && (
            <div className="super-action-row">
              <label className="detail-label">Extend trial by</label>
              <input type="number" className="form-input" value={trialDays} onChange={e => setTrialDays(e.target.value)} min={1} max={365} style={{ width: 80 }} />
              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>days</span>
              <button
                className="btn btn-sm btn-orange"
                onClick={() => doAction('extend-trial', { days: parseInt(trialDays) })}
                disabled={!!actionLoading}
              >
                {actionLoading === 'extend-trial' ? 'Extending...' : 'Extend'}
              </button>
            </div>
          )}

          {/* Comp / Remove Comp */}
          {!billing.no_subscription && <div className="super-action-row">
            {billing.comped ? (
              <button
                className="btn btn-sm btn-outline"
                onClick={() => doAction('remove-comp', {})}
                disabled={!!actionLoading}
              >
                {actionLoading === 'remove-comp' ? 'Removing...' : 'Remove Comp'}
              </button>
            ) : (
              <button
                className="btn btn-sm btn-orange"
                onClick={() => doAction('comp', {})}
                disabled={!!actionLoading}
              >
                {actionLoading === 'comp' ? 'Comping...' : 'Comp Account'}
              </button>
            )}
          </div>}

          {/* Discount */}
          {!billing.no_subscription && !billing.comped && (
            <div className="super-action-row">
              {showDiscount ? (
                <form onSubmit={handleDiscount} className="super-discount-form">
                  <select
                    className="form-input"
                    value={discountForm.type}
                    onChange={e => setDiscountForm({ ...discountForm, type: e.target.value })}
                    style={{ width: 140 }}
                  >
                    <option value="percent_off">% off</option>
                    <option value="amount_off">$ off</option>
                  </select>
                  <input
                    className="form-input"
                    type="number"
                    placeholder={discountForm.type === 'percent_off' ? '10' : '5.00'}
                    value={discountForm.value}
                    onChange={e => setDiscountForm({ ...discountForm, value: e.target.value })}
                    min={1}
                    step={discountForm.type === 'amount_off' ? '0.01' : '1'}
                    required
                    style={{ width: 80 }}
                  />
                  <select
                    className="form-input"
                    value={discountForm.duration}
                    onChange={e => setDiscountForm({ ...discountForm, duration: e.target.value })}
                    style={{ width: 130 }}
                  >
                    <option value="once">One-time</option>
                    <option value="repeating">Repeating</option>
                    <option value="forever">Forever</option>
                  </select>
                  {discountForm.duration === 'repeating' && (
                    <>
                      <input
                        className="form-input"
                        type="number"
                        value={discountForm.duration_in_months}
                        onChange={e => setDiscountForm({ ...discountForm, duration_in_months: e.target.value })}
                        min={1}
                        style={{ width: 60 }}
                      />
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>months</span>
                    </>
                  )}
                  <button type="submit" className="btn btn-sm btn-orange" disabled={!!actionLoading}>
                    {actionLoading === 'discount' ? 'Applying...' : 'Apply'}
                  </button>
                  <button type="button" className="btn btn-sm btn-outline" onClick={() => setShowDiscount(false)}>Cancel</button>
                </form>
              ) : (
                <button className="btn btn-sm btn-outline" onClick={() => setShowDiscount(true)}>Apply Discount</button>
              )}
            </div>
          )}

          {/* Reactivate (shown when deactivated or suspended) */}
          {(billing.deactivated || billing.status === 'suspended' || billing.manually_suspended) && (
            <div className="super-action-row">
              <button
                className="btn btn-sm btn-orange"
                onClick={() => doAction('reactivate', {})}
                disabled={!!actionLoading}
              >
                {actionLoading === 'reactivate' ? 'Reactivating...' : 'Reactivate'}
              </button>
            </div>
          )}

          {/* Deactivate */}
          {!billing.deactivated && (
            <div className="super-action-row">
              <button
                className="btn btn-sm btn-danger"
                onClick={() => { if (window.confirm('Deactivate this account? All logins (admins, coaches, players) will be blocked. Data will be preserved.')) doAction('deactivate', {}); }}
                disabled={!!actionLoading}
              >
                {actionLoading === 'deactivate' ? 'Deactivating...' : 'Deactivate'}
              </button>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Blocks all logins. Data preserved.</span>
            </div>
          )}

          {/* Delete */}
          <div className="super-action-row">
            <button
              className="btn btn-sm btn-danger"
              onClick={() => { if (window.confirm('PERMANENTLY DELETE this account and ALL its data (teams, players, completions)? This cannot be undone.')) { if (window.confirm('Are you absolutely sure? This is irreversible.')) doAction('delete', {}, 'Account deleted.'); } }}
              disabled={!!actionLoading}
              style={{ opacity: 0.7 }}
            >
              {actionLoading === 'delete' ? 'Deleting...' : 'Delete Forever'}
            </button>
            <span style={{ color: '#b91c1c', fontSize: '0.8rem' }}>Permanently removes all data.</span>
          </div>
        </div>
      </div>

      {/* Scholarships & Discounts */}
      {teams.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12 }}>Scholarships & Discounts</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 16 }}>
            Generate one-time-use invite links. Scholarships give free access. Discounts apply a pre-configured Stripe coupon.
          </p>
          <form onSubmit={handleGeneratePromos} className="super-discount-form" style={{ flexWrap: 'wrap', gap: 8 }}>
            <select
              className="form-input"
              value={promoTeamId}
              onChange={e => setPromoTeamId(e.target.value)}
              required
              style={{ width: 180 }}
            >
              <option value="">Select team...</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <select
              className="form-input"
              value={promoType}
              onChange={e => setPromoType(e.target.value)}
              style={{ width: 160 }}
            >
              <option value="scholarship">Scholarship (free)</option>
              <option value="discount">Discount</option>
            </select>
            {promoType === 'discount' && (
              <select
                className="form-input"
                value={promoDiscount}
                onChange={e => setPromoDiscount(e.target.value)}
                style={{ width: 120 }}
              >
                <option value={50}>50% off</option>
                <option value={75}>75% off</option>
              </select>
            )}
            <input
              className="form-input"
              type="number"
              value={promoCount}
              onChange={e => setPromoCount(e.target.value)}
              min={1}
              max={20}
              style={{ width: 60 }}
            />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>links</span>
            <button type="submit" className="btn btn-sm btn-orange" disabled={promoLoading || !promoTeamId}>
              {promoLoading ? 'Generating...' : 'Generate'}
            </button>
          </form>
          {promoError && <div className="super-action-msg error" style={{ marginTop: 8 }}>{promoError}</div>}
          {promoLinks.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  {promoLinks.length} {promoType} link{promoLinks.length !== 1 ? 's' : ''} generated
                </span>
                <button className="btn btn-sm btn-outline" onClick={handleCopyAllLinks} type="button">
                  {promoCopied ? 'Copied!' : 'Copy All Links'}
                </button>
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 12,
                maxHeight: 200,
                overflowY: 'auto',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                lineHeight: 1.8,
                wordBreak: 'break-all',
              }}>
                {promoLinks.map((l, i) => (
                  <div key={i}>{l.url}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// CREATE CLUB FORM (preserved from Build 3)
// ============================================================
function CreateClubModal({ apiFetch, onClose, onSuccess }) {
  const [form, setForm] = useState({ club_name: '', admin_email: '', admin_first_name: '', admin_last_name: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiFetch('/api/super/clubs', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      onSuccess(`Club "${form.club_name}" created. Invitation sent to ${form.admin_email}.`);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>Create Club</h2>
        {error && <div style={{ color: '#e74c3c', marginBottom: 12 }}>{error}</div>}
        <form onSubmit={handleCreate}>
          <div className="form-group">
            <label className="form-label">Club Name</label>
            <input className="form-input" value={form.club_name} onChange={e => setForm({ ...form, club_name: e.target.value })} placeholder="e.g. Lightning FC" required />
          </div>
          <div className="form-group">
            <label className="form-label">Club Admin Email</label>
            <input className="form-input" type="email" value={form.admin_email} onChange={e => setForm({ ...form, admin_email: e.target.value })} autoCapitalize="none" required />
          </div>
          <div className="form-group">
            <label className="form-label">Admin First Name</label>
            <input className="form-input" value={form.admin_first_name} onChange={e => setForm({ ...form, admin_first_name: e.target.value })} required />
          </div>
          <div className="form-group">
            <label className="form-label">Admin Last Name</label>
            <input className="form-input" value={form.admin_last_name} onChange={e => setForm({ ...form, admin_last_name: e.target.value })} required />
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>
            The club admin will receive an email invitation to set their password and complete MFA setup.
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Creating...' : 'Create Club'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// CREATE TEAM FORM
// ============================================================
function CreateTeamModal({ apiFetch, onClose, onSuccess }) {
  const [form, setForm] = useState({ team_name: '', coach_email: '', coach_first_name: '', coach_last_name: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiFetch('/api/super/teams', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      onSuccess(`Team "${form.team_name}" created. Invitation sent to ${form.coach_email}.`);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>Add Team</h2>
        {error && <div style={{ color: '#e74c3c', marginBottom: 12 }}>{error}</div>}
        <form onSubmit={handleCreate}>
          <div className="form-group">
            <label className="form-label">Team Name</label>
            <input className="form-input" value={form.team_name} onChange={e => setForm({ ...form, team_name: e.target.value })} placeholder="e.g. U12 Lightning" required />
          </div>
          <div className="form-group">
            <label className="form-label">Coach Email</label>
            <input className="form-input" type="email" value={form.coach_email} onChange={e => setForm({ ...form, coach_email: e.target.value })} autoCapitalize="none" required />
          </div>
          <div className="form-group">
            <label className="form-label">Coach First Name</label>
            <input className="form-input" value={form.coach_first_name} onChange={e => setForm({ ...form, coach_first_name: e.target.value })} required />
          </div>
          <div className="form-group">
            <label className="form-label">Coach Last Name</label>
            <input className="form-input" value={form.coach_last_name} onChange={e => setForm({ ...form, coach_last_name: e.target.value })} required />
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>
            The coach will receive an email invitation to set their password and get started.
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Creating...' : 'Add Team'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// BILLING ALERTS VIEW
// ============================================================
function BillingAlerts({ apiFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  const loadAlerts = useCallback(async () => {
    try {
      const d = await apiFetch('/api/super/billing-alerts');
      setData(d);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const doAction = async (action, id) => {
    setActionLoading(`${action}-${id}`);
    setActionMsg('');
    try {
      await apiFetch(`/api/super/billing-alerts/${id}/${action}`, { method: 'POST' });
      setActionMsg(action === 'extend-grace' ? 'Grace period extended.' : 'Payment retry initiated.');
      loadAlerts();
    } catch (err) {
      setActionMsg(`Error: ${err.message}`);
    } finally {
      setActionLoading('');
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!data) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Failed to load billing alerts.</div>;

  return (
    <div>
      {actionMsg && <div className="super-action-msg" style={{ marginBottom: 16 }}>{actionMsg}</div>}

      {/* Failed Payments */}
      <div className="billing-alerts-section">
        <div className="billing-alerts-title">Failed Payments ({data.failed_payments.length})</div>
        {data.failed_payments.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '12px 0' }}>No failed payments.</div>
        ) : data.failed_payments.map(fp => {
          const daysRemaining = Math.max(0, 7 - (fp.days_past_due || 0));
          return (
            <div key={fp.id} className={`billing-alert-row ${daysRemaining <= 2 ? 'urgent' : ''}`}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{fp.account_name}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {fp.owner_type} &middot; {fp.card_brand} ****{fp.card_last4} &middot; Past due {fp.days_past_due}d
                </div>
              </div>
              <div className={`grace-remaining ${daysRemaining <= 2 ? 'critical' : ''}`}>
                {daysRemaining}d left
              </div>
              <div style={{ display: 'flex', gap: 6, marginLeft: 12 }}>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => doAction('extend-grace', fp.id)}
                  disabled={!!actionLoading}
                >
                  {actionLoading === `extend-grace-${fp.id}` ? '...' : 'Extend'}
                </button>
                <button
                  className="btn btn-sm btn-orange"
                  onClick={() => doAction('retry-payment', fp.id)}
                  disabled={!!actionLoading}
                >
                  {actionLoading === `retry-payment-${fp.id}` ? '...' : 'Retry'}
                </button>
                {fp.contact_email && (
                  <a href={`mailto:${fp.contact_email}`} className="btn btn-sm btn-outline">Contact</a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Expiring Cards */}
      <div className="billing-alerts-section">
        <div className="billing-alerts-title">Expiring Cards ({data.expiring_cards.length})</div>
        {data.expiring_cards.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '12px 0' }}>No cards expiring in the next 30 days.</div>
        ) : data.expiring_cards.map(ec => {
          const expiryDate = new Date(ec.card_exp_year, ec.card_exp_month, 0);
          const daysUntil = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
          return (
            <div key={ec.id} className={`billing-alert-row ${daysUntil <= 1 ? 'urgent' : ''}`}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{ec.account_name}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {ec.owner_type} &middot; {ec.card_brand} ****{ec.card_last4} &middot; Expires {ec.card_exp_month}/{ec.card_exp_year}
                </div>
              </div>
              <div className={`grace-remaining ${daysUntil <= 7 ? 'critical' : ''}`}>
                {daysUntil}d
              </div>
              {ec.contact_email && (
                <a href={`mailto:${ec.contact_email}`} className="btn btn-sm btn-outline" style={{ marginLeft: 12 }}>Contact</a>
              )}
            </div>
          );
        })}
      </div>

      {/* Recently Deactivated */}
      <div className="billing-alerts-section">
        <div className="billing-alerts-title">Recently Deactivated ({data.deactivated.length})</div>
        {data.deactivated.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '12px 0' }}>No deactivations in the last 30 days.</div>
        ) : data.deactivated.map((d, i) => (
          <div key={`${d.id}-${i}`} className="billing-alert-row">
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{d.account_name}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {d.owner_type} &middot; Deactivated {formatDate(d.deactivated_at)}
              </div>
            </div>
            {d.contact_email && (
              <a href={`mailto:${d.contact_email}`} className="btn btn-sm btn-outline" style={{ marginLeft: 12 }}>Contact</a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// ADMIN USERS VIEW
// ============================================================
function AdminUsers({ apiFetch }) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleting, setDeleting] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/users');
      setUsers(data.users);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await apiFetch('/api/super/users', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setSuccess(`Admin user "${form.first_name} ${form.last_name}" created.`);
      setForm({ email: '', password: '', first_name: '', last_name: '' });
      setShowForm(false);
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete admin user "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    setError('');
    setSuccess('');
    try {
      await apiFetch(`/api/super/users/${id}`, { method: 'DELETE' });
      setSuccess(`User "${name}" deleted.`);
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting('');
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      {success && <div className="super-action-msg success" style={{ marginBottom: 16 }}>{success}</div>}
      {error && <div className="super-action-msg error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="flex-between mb-16">
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{users.length} admin user{users.length !== 1 ? 's' : ''}</div>
        <button className="btn btn-orange btn-sm" onClick={() => setShowForm(true)}>+ Add User</button>
      </div>

      <div className="super-table-wrap">
        <table className="super-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="super-table-row">
                <td style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}{u.id === currentUser?.id ? <span style={{ color: 'var(--orange)', fontSize: '0.8rem', marginLeft: 8 }}>(you)</span> : ''}</td>
                <td>{u.email}</td>
                <td>{formatDate(u.created_at)}</td>
                <td style={{ textAlign: 'right' }}>
                  {u.id !== currentUser?.id && (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(u.id, `${u.first_name} ${u.last_name}`)}
                      disabled={!!deleting}
                    >
                      {deleting === u.id ? '...' : 'Delete'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Add Admin User</h2>
            {error && <div style={{ color: '#e74c3c', marginBottom: 12 }}>{error}</div>}
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input className="form-input" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input className="form-input" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} autoCapitalize="none" required />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} minLength={8} required />
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Minimum 8 characters. Share this password with the user manually.</div>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>
                This user will have full super admin privileges. No email will be sent — share the credentials manually.
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Creating...' : 'Create User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// SECURITY PANEL (locked accounts, password reset, unlock)
// ============================================================
function SecurityPanel({ apiFetch, startImpersonation, navigate }) {
  const [locked, setLocked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [actionError, setActionError] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Reset password state
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const loadLocked = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/locked-accounts');
      setLocked(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { loadLocked(); }, [loadLocked]);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ q: searchQuery });
        if (searchType) params.set('type', searchType);
        const data = await apiFetch(`/api/super/search-users?${params}`);
        setSearchResults(data.results || []);
      } catch (err) {
        console.error(err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchType, apiFetch]);

  const handleUnlock = async (userType, userId) => {
    setActionMsg('');
    setActionError('');
    try {
      const data = await apiFetch('/api/super/unlock-account', {
        method: 'POST',
        body: JSON.stringify({ user_type: userType, user_id: userId }),
      });
      setActionMsg(data.message);
      loadLocked();
    } catch (err) {
      setActionError(err.message || 'Failed to unlock account');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;
    setResetting(true);
    setActionMsg('');
    setActionError('');
    try {
      const data = await apiFetch('/api/super/reset-password', {
        method: 'POST',
        body: JSON.stringify({ user_type: selectedUser.user_type, user_id: selectedUser.id, new_password: newPassword }),
      });
      setActionMsg(data.message);
      setNewPassword('');
      setSelectedUser(null);
      setSearchQuery('');
      setSearchResults([]);
      loadLocked();
    } catch (err) {
      setActionError(err.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const getUserLabel = (u) => {
    const name = `${u.first_name} ${u.last_name}`;
    const identifier = u.email || u.username || u.generated_username || '';
    return identifier ? `${name} (${identifier})` : name;
  };

  const getTypeLabel = (t) => t === 'staff' ? 'Staff' : t === 'parent' ? 'Parent' : 'Player';

  const getLockLabel = (u) => {
    if (u.permanently_locked) return 'Permanent';
    if (u.locked_until) {
      const remaining = Math.ceil((new Date(u.locked_until) - new Date()) / 60000);
      return remaining > 0 ? `${remaining} min remaining` : 'Expired';
    }
    return '—';
  };

  const handleLoginAs = async (u) => {
    try {
      const data = await apiFetch('/api/super/impersonate', {
        method: 'POST',
        body: JSON.stringify({ user_id: u.id, user_type: u.user_type }),
      });
      startImpersonation(data.token, data.user, data.teams);
      if (u.user_type === 'player') navigate('/today');
      else if (u.user_type === 'parent') navigate('/parent');
      else if (data.user.role === 'club_admin') navigate('/club');
      else navigate('/admin');
    } catch (err) {
      setActionError(err.message || 'Failed to login as user');
    }
  };

  const allLocked = locked ? [...locked.staff, ...locked.parents, ...locked.players] : [];

  return (
    <div>
      {actionMsg && <div style={{ background: 'rgba(46,204,113,0.15)', color: '#2ecc71', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{actionMsg}</div>}
      {actionError && <div style={{ background: 'rgba(231,76,60,0.15)', color: '#e74c3c', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{actionError}</div>}

      {/* Locked Accounts */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 16px' }}>Locked Accounts</h3>
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : allLocked.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', padding: '20px 0' }}>No locked accounts.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Failed Attempts</th>
                  <th>Lock Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {allLocked.map(u => (
                  <tr key={`${u.user_type}-${u.id}`}>
                    <td>{getUserLabel(u)}</td>
                    <td>{getTypeLabel(u.user_type)}</td>
                    <td>{u.failed_login_attempts}</td>
                    <td>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem',
                        background: u.permanently_locked ? 'rgba(231,76,60,0.2)' : 'rgba(241,196,15,0.2)',
                        color: u.permanently_locked ? '#e74c3c' : '#f1c40f',
                      }}>
                        {getLockLabel(u)}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline" onClick={() => handleUnlock(u.user_type, u.id)}>
                        Unlock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reset Password */}
      <div className="card">
        <h3 style={{ margin: '0 0 16px' }}>Reset Password</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <select className="form-input" value={searchType} onChange={e => setSearchType(e.target.value)} style={{ maxWidth: 140 }}>
            <option value="">All types</option>
            <option value="staff">Staff</option>
            <option value="player">Player</option>
            <option value="parent">Parent</option>
          </select>
          <input
            className="form-input"
            placeholder="Search by name, email, or username..."
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setSelectedUser(null); }}
            style={{ flex: 1 }}
          />
        </div>

        {searching && <div style={{ color: 'var(--text-muted)', marginBottom: 8 }}>Searching...</div>}

        {searchResults.length > 0 && !selectedUser && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12, maxHeight: 200, overflowY: 'auto' }}>
            {searchResults.map(u => (
              <div
                key={`${u.user_type}-${u.id}`}
                style={{
                  padding: '10px 14px', borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ cursor: 'pointer', flex: 1 }} onClick={() => { setSelectedUser(u); setSearchResults([]); }}>{getUserLabel(u)}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{getTypeLabel(u.user_type)}</span>
                  {(u.user_type === 'player' || u.user_type === 'parent') && (
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={(e) => { e.stopPropagation(); handleLoginAs(u); }}
                      style={{ fontSize: '0.7rem', padding: '2px 8px', whiteSpace: 'nowrap' }}
                    >
                      Login as
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedUser && (
          <form onSubmit={handleResetPassword}>
            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>
              <strong>{getUserLabel(selectedUser)}</strong>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 8 }}>{getTypeLabel(selectedUser.user_type)}</span>
              <button type="button" onClick={() => { setSelectedUser(null); setNewPassword(''); }} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>✕</button>
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                className="form-input"
                type="text"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder={selectedUser.user_type === 'staff' ? '12+ chars, letter + number' : selectedUser.user_type === 'parent' ? '8+ characters' : '6+ characters'}
                required
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                This also unlocks the account if locked. Share the new password with the user manually.
              </div>
            </div>
            <button type="submit" className="btn btn-orange" disabled={resetting}>
              {resetting ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ============================================================
// MAIN SUPER ADMIN COMPONENT
// ============================================================
export default function SuperAdmin() {
  const { user, apiFetch, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [totals, setTotals] = useState({ active_accounts: 0, mrr: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedIndividualId, setSelectedIndividualId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('clubs'); // 'clubs' | 'teams' | 'players' | 'individuals' | 'billing' | 'users' | 'security'
  const [showInactive, setShowInactive] = useState(false);
  const [individualAccounts, setIndividualAccounts] = useState([]);
  const [individualsLoading, setIndividualsLoading] = useState(true);

  const loadAccounts = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/accounts');
      setAccounts(data.accounts);
      setTotals(data.totals);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const loadIndividuals = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/individual-accounts');
      setIndividualAccounts(data.accounts);
    } catch (err) {
      console.error(err);
    } finally {
      setIndividualsLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { loadAccounts(); loadIndividuals(); }, [loadAccounts, loadIndividuals]);

  const clubs = accounts.filter(a => a.owner_type === 'club');
  const teams = accounts.filter(a => a.owner_type === 'team');

  return (
    <div className="admin-layout">
      <SuperAdminNav
        activeItem={['billing', 'users', 'security'].includes(view) ? view : null}
        onViewToggle={(v) => { setView(view === v ? 'clubs' : v); setSelectedAccountId(null); setSelectedIndividualId(null); }}
      />
      <div className="admin-page">
        {view === 'billing' ? (
          <>
            <h1 className="page-title" style={{ marginBottom: 16 }}>Billing Alerts</h1>
            <BillingAlerts apiFetch={apiFetch} />
          </>
        ) : view === 'users' ? (
          <>
            <h1 className="page-title" style={{ marginBottom: 16 }}>Admin Users</h1>
            <AdminUsers apiFetch={apiFetch} />
          </>
        ) : view === 'security' ? (
          <>
            <h1 className="page-title" style={{ marginBottom: 16 }}>Security</h1>
            <SecurityPanel apiFetch={apiFetch} startImpersonation={startImpersonation} navigate={navigate} />
          </>
        ) : (
          <>
            {/* Summary cards */}
            {!selectedAccountId && !selectedIndividualId && (
              <div className="super-totals">
                <div className="super-total-card">
                  <div className="super-total-value">{totals.active_accounts}</div>
                  <div className="super-total-label">Active + Trialing</div>
                </div>
                <div className="super-total-card">
                  <div className="super-total-value">{formatCurrency(totals.mrr)}</div>
                  <div className="super-total-label">Monthly-Equivalent Revenue</div>
                </div>
              </div>
            )}

            {/* Clubs / Teams / Individuals tabs */}
            {!selectedAccountId && !selectedIndividualId && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 0 }}>
                  <button
                    className={`btn btn-sm ${view === 'clubs' ? 'btn-orange' : 'btn-outline'}`}
                    onClick={() => { setView('clubs'); setSelectedIndividualId(null); }}
                    style={{ borderRadius: '6px 0 0 6px' }}
                  >
                    Clubs ({clubs.filter(a => showInactive || !a.deactivated).length})
                  </button>
                  <button
                    className={`btn btn-sm ${view === 'teams' ? 'btn-orange' : 'btn-outline'}`}
                    onClick={() => { setView('teams'); setSelectedIndividualId(null); }}
                    style={{ borderRadius: 0 }}
                  >
                    Teams ({teams.filter(a => showInactive || !a.deactivated).length})
                  </button>
                  <button
                    className={`btn btn-sm ${view === 'players' ? 'btn-orange' : 'btn-outline'}`}
                    onClick={() => { setView('players'); setSelectedAccountId(null); setSelectedIndividualId(null); }}
                    style={{ borderRadius: 0 }}
                  >
                    Club/Team Players
                  </button>
                  <button
                    className={`btn btn-sm ${view === 'individuals' ? 'btn-orange' : 'btn-outline'}`}
                    onClick={() => { setView('individuals'); setSelectedIndividualId(null); }}
                    style={{ borderRadius: '0 6px 6px 0' }}
                  >
                    Individuals/Families ({individualAccounts.length})
                  </button>
                </div>
                {view === 'clubs' && (
                  <button className="btn btn-orange btn-sm" onClick={() => setShowCreate(true)}>+ Create Club</button>
                )}
                {view === 'teams' && (
                  <button className="btn btn-orange btn-sm" onClick={() => setShowCreateTeam(true)}>+ Add Team</button>
                )}
              </div>
            )}

            {success && <div style={{ color: 'var(--success)', marginBottom: 16, padding: '12px 16px', background: 'rgba(46,204,113,0.1)', borderRadius: 8 }}>{success}</div>}

            {selectedIndividualId ? (
              <IndividualDetail
                parentId={selectedIndividualId}
                onBack={() => { setSelectedIndividualId(null); loadIndividuals(); }}
                apiFetch={apiFetch}
              />
            ) : selectedAccountId ? (
              <AccountDetail
                accountId={selectedAccountId}
                onBack={() => { setSelectedAccountId(null); loadAccounts(); }}
                apiFetch={apiFetch}
              />
            ) : view === 'clubs' ? (
              <AccountsTable
                accounts={clubs}
                onSelect={a => setSelectedAccountId(a.id)}
                showInactive={showInactive}
                setShowInactive={setShowInactive}
                loading={loading}
                label="Clubs"
              />
            ) : view === 'teams' ? (
              <AccountsTable
                accounts={teams}
                onSelect={a => setSelectedAccountId(a.id)}
                showInactive={showInactive}
                setShowInactive={setShowInactive}
                loading={loading}
                label="Teams"
              />
            ) : view === 'players' ? (
              <ClubTeamPlayersTable apiFetch={apiFetch} startImpersonation={startImpersonation} navigate={navigate} />
            ) : (
              <IndividualsTable
                accounts={individualAccounts}
                loading={individualsLoading}
                onSelect={a => setSelectedIndividualId(a.id)}
              />
            )}

            {!selectedAccountId && !selectedIndividualId && (
              <div className="card" style={{ textAlign: 'left', marginTop: 24 }}>
                <div style={{ marginBottom: 8 }}><strong>Logged in as:</strong></div>
                <div>{user?.first_name} {user?.last_name}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{user?.email}</div>
                <div style={{ color: 'var(--orange)', fontSize: '0.85rem', marginTop: 4 }}>Role: {user?.role}</div>
              </div>
            )}

            {showCreate && (
              <CreateClubModal
                apiFetch={apiFetch}
                onClose={() => setShowCreate(false)}
                onSuccess={(msg) => { setSuccess(msg); loadAccounts(); }}
              />
            )}

            {showCreateTeam && (
              <CreateTeamModal
                apiFetch={apiFetch}
                onClose={() => setShowCreateTeam(false)}
                onSuccess={(msg) => { setSuccess(msg); loadAccounts(); }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
